import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { DecisionStatus } from "@/gen/omnios/v1/accounting_pb";
import {
	useApproveDecision,
	useListDecisions,
	useRejectDecision,
} from "@/hooks/use-accounting";
import { formatRelative } from "@/lib/format";
import { useNotificationStore } from "@/lib/notifications";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Bell, CheckCheck, CheckCircle2, XCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type Tab = "notifications" | "approvals";

export function NotificationBell() {
	const {
		items,
		unreadCount,
		fetchUnreadCount,
		fetchNotifications,
		markRead,
		markAllRead,
	} = useNotificationStore();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [activeTab, setActiveTab] = useState<Tab>("notifications");

	// Fetch pending decisions for approvals tab
	const { data: decisionsData } = useListDecisions({
		status: DecisionStatus.PROPOSED,
	});
	const decisions = decisionsData?.decisions ?? [];
	const approveMutation = useApproveDecision();
	const rejectMutation = useRejectDecision();

	const totalBadge = unreadCount + decisions.length;

	useQuery({
		queryKey: ["notification-unread-count"],
		queryFn: async () => {
			await fetchUnreadCount();
			return null;
		},
		refetchInterval: 60_000,
	});

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="relative"
					onClick={() => fetchNotifications()}
					aria-label={t("notifications")}
				>
					<Bell className="h-4 w-4" />
					{totalBadge > 0 && (
						<span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
							{totalBadge > 99 ? "99+" : totalBadge}
						</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-80 p-0">
				{/* Tabs */}
				<div className="flex items-center border-b">
					<Button
						type="button"
						variant="ghost"
						onClick={() => setActiveTab("notifications")}
						className={cn(
							"flex-1 px-3 py-2 text-xs font-medium border-b-2 rounded-none h-auto",
							activeTab === "notifications"
								? "border-primary text-foreground"
								: "border-transparent text-muted-foreground hover:text-foreground",
						)}
					>
						{t("notifications")}
						{unreadCount > 0 && (
							<span className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary/10 text-primary px-1 text-[10px]">
								{unreadCount}
							</span>
						)}
					</Button>
					<Button
						type="button"
						variant="ghost"
						onClick={() => setActiveTab("approvals")}
						className={cn(
							"flex-1 px-3 py-2 text-xs font-medium border-b-2 rounded-none h-auto",
							activeTab === "approvals"
								? "border-primary text-foreground"
								: "border-transparent text-muted-foreground hover:text-foreground",
						)}
					>
						{t("approvals")}
						{decisions.length > 0 && (
							<span className="ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500/10 text-amber-600 px-1 text-[10px]">
								{decisions.length}
							</span>
						)}
					</Button>
				</div>

				{/* Content */}
				<div className="max-h-80 overflow-y-auto">
					{activeTab === "notifications" ? (
						<>
							{unreadCount > 0 && (
								<div className="flex justify-end px-3 py-1.5 border-b">
									<Button
										variant="ghost"
										size="sm"
										className="h-6 text-xs gap-1"
										onClick={() => markAllRead()}
									>
										<CheckCheck className="h-3 w-3" />
										{t("mark_all_read")}
									</Button>
								</div>
							)}
							{items.length === 0 ? (
								<p className="py-8 text-center text-sm text-muted-foreground">
									{t("no_notifications")}
								</p>
							) : (
								items.map((n) => (
									<Button
										key={n.id}
										type="button"
										variant="ghost"
										className={cn(
											"flex w-full gap-2 px-3 py-2.5 text-left h-auto rounded-none justify-start border-b last:border-b-0",
											!n.isRead && "bg-primary/5",
										)}
										onClick={() => {
											if (!n.isRead) markRead([n.id]);
											if (n.link) navigate({ to: n.link });
										}}
									>
										<div className="flex-1 min-w-0">
											<p className="text-sm font-medium truncate">{n.title}</p>
											<p className="text-xs text-muted-foreground truncate">
												{n.body}
											</p>
											<p className="text-[10px] text-muted-foreground/60 mt-0.5">
												{formatRelative(n.createdAt)}
											</p>
										</div>
										{!n.isRead && (
											<div className="h-2 w-2 rounded-full bg-primary shrink-0 mt-1.5" />
										)}
									</Button>
								))
							)}
						</>
					) : (
						<>
							{decisions.length === 0 ? (
								<p className="py-8 text-center text-sm text-muted-foreground">
									{t("no_pending_approvals")}
								</p>
							) : (
								decisions.map((d) => (
									<div
										key={d.id}
										className="px-3 py-2.5 border-b last:border-b-0"
									>
										<div className="flex items-start gap-2">
											<div className="flex-1 min-w-0">
												<p className="text-sm font-medium">
													{t(`decision_type_${d.decisionType}`, {
														ns: "accounting",
														defaultValue: d.decisionType,
													})}
												</p>
												<p className="text-xs text-muted-foreground">
													{d.agentName} ·{" "}
													<span
														className={cn(
															"tabular-nums",
															d.confidence >= 0.9
																? "text-green-600"
																: d.confidence >= 0.7
																	? "text-amber-500"
																	: "text-red-500",
														)}
													>
														{Math.round(d.confidence * 100)}%
													</span>
												</p>
												{d.entryId && (
													<Button
														type="button"
														variant="link"
														size="sm"
														className="text-[10px] text-primary h-auto px-0 mt-0.5"
														onClick={() =>
															navigate({
																to: "/accounting/journal/$id",
																params: { id: d.entryId },
															})
														}
													>
														{t("view_entry")}
													</Button>
												)}
											</div>
											<div className="flex items-center gap-1 shrink-0">
												<Button
													type="button"
													variant="ghost"
													size="icon"
													className="h-7 w-7 text-green-600 hover:bg-green-500/10"
													onClick={() => approveMutation.mutate({ id: d.id })}
													disabled={approveMutation.isPending}
													title={t("approve")}
												>
													<CheckCircle2 className="h-4 w-4" />
												</Button>
												<Button
													type="button"
													variant="ghost"
													size="icon"
													className="h-7 w-7 text-red-500 hover:bg-red-500/10"
													onClick={() =>
														rejectMutation.mutate({
															id: d.id,
															reason: "",
														})
													}
													disabled={rejectMutation.isPending}
													title={t("reject")}
												>
													<XCircle className="h-4 w-4" />
												</Button>
											</div>
										</div>
									</div>
								))
							)}
						</>
					)}
				</div>
				{activeTab === "notifications" && (
					<div className="border-t">
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="w-full h-8 rounded-none rounded-b-md text-xs text-muted-foreground"
							onClick={() => {
								setOpen(false);
								navigate({ to: "/notifications" });
							}}
						>
							{t("all_notifications")}
						</Button>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
}
